import { useEffect, useRef, useState } from "react";

// jsdom and older engines have no matchMedia; treat a miss as "no preference"
const media = (query) =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(query).matches;

const canHover = () => media("(hover: hover) and (pointer: fine)");
const reduced = () => media("(prefers-reduced-motion: reduce)");

/** Live subscription to the reduced-motion preference. */
export function usePrefersReducedMotion() {
  const [pref, setPref] = useState(reduced);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setPref(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  return pref;
}

/**
 * Adds .is-in once the node scrolls into view, then stops observing.
 * Returns a ref; pair with the .reveal class.
 */
export function useReveal({ threshold = 0.16, rootMargin = "0px 0px -8% 0px" } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (!("IntersectionObserver" in window)) {
      el.classList.add("is-in");
      return undefined;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        el.classList.add("is-in");
        io.unobserve(el);
      },
      { threshold, rootMargin }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin]);

  return ref;
}

/**
 * Reveals a container and staggers any [data-stagger] descendants.
 */
export function useStagger({ step = 90, threshold = 0.12 } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const kids = el.querySelectorAll("[data-stagger]");
    kids.forEach((k, i) => k.style.setProperty("--reveal-delay", `${i * step}ms`));

    if (!("IntersectionObserver" in window)) {
      el.classList.add("is-in");
      kids.forEach((k) => k.classList.add("is-in"));
      return undefined;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        el.classList.add("is-in");
        kids.forEach((k) => k.classList.add("is-in"));
        io.unobserve(el);
      },
      { threshold, rootMargin: "0px 0px -6% 0px" }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [step, threshold]);

  return ref;
}

/**
 * Pointer parallax across a whole section. Writes --px / --py (-1..1)
 * on the container; children position themselves off those values.
 */
export function usePointerParallax() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || reduced() || !canHover()) return undefined;

    let frame = 0;

    const onMove = (e) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        el.style.setProperty("--px", px.toFixed(3));
        el.style.setProperty("--py", py.toFixed(3));
      });
    };

    const onLeave = () => {
      el.style.setProperty("--px", "0");
      el.style.setProperty("--py", "0");
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);

    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return ref;
}

/** Scroll progress 0..1 across a section — drives the sticky routine. */
export function useScrollProgress() {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    let frame = 0;

    const measure = () => {
      frame = 0;
      const r = el.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      if (total <= 0) return setProgress(0);
      setProgress(Math.min(1, Math.max(0, -r.top / total)));
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return [ref, progress];
}

/** Button that leans toward the cursor. */
export function useMagnetic({ strength = 0.32, radius = 90 } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || reduced() || !canHover()) return undefined;

    let frame = 0;

    const onMove = (e) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const r = el.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height / 2);
        if (Math.hypot(dx, dy) > radius + Math.max(r.width, r.height) / 2) return;
        el.style.setProperty("--mx", `${(dx * strength).toFixed(1)}px`);
        el.style.setProperty("--my", `${(dy * strength).toFixed(1)}px`);
      });
    };

    const onEnter = () => el.classList.add("is-tracking");

    const onLeave = () => {
      el.classList.remove("is-tracking");
      el.style.setProperty("--mx", "0px");
      el.style.setProperty("--my", "0px");
    };

    el.addEventListener("pointerenter", onEnter);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);

    return () => {
      el.removeEventListener("pointerenter", onEnter);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [strength, radius]);

  return ref;
}

/** Header state: solid after a threshold, hidden while scrolling down. */
export function useScrollDirection(threshold = 80) {
  const [state, setState] = useState({ solid: false, hidden: false });

  useEffect(() => {
    let last = window.scrollY;
    let frame = 0;

    const measure = () => {
      frame = 0;
      const y = window.scrollY;
      const solid = y > threshold;
      const hidden = y > 260 && y > last + 4;
      const shown = y < last - 4;
      last = y;
      setState((s) =>
        s.solid === solid && s.hidden === (hidden && !shown)
          ? s
          : { solid, hidden: hidden && !shown }
      );
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [threshold]);

  return state;
}

/**
 * Writes --p (0..1) onto the node as it travels through the viewport:
 * 0 the moment its top enters from below, 1 as its bottom leaves the top.
 * CSS reads --p to drive parallax, zoom, clip and colour — keeping the
 * scroll work on the compositor rather than in React state.
 */
export function useScrollScrub() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (reduced()) {
      el.style.setProperty("--p", "0.5");
      return undefined;
    }

    let frame = 0;
    let visible = true;

    const measure = () => {
      frame = 0;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const total = r.height + vh;
      const p = total ? (vh - r.top) / total : 0;
      el.style.setProperty("--p", Math.min(1, Math.max(0, p)).toFixed(4));
    };

    const onScroll = () => {
      if (frame || !visible) return;
      frame = requestAnimationFrame(measure);
    };

    // stop measuring entirely while the section is nowhere near the viewport
    const io =
      "IntersectionObserver" in window
        ? new IntersectionObserver(
            ([e]) => {
              visible = e.isIntersecting;
              if (visible) onScroll();
            },
            { rootMargin: "120px 0px" }
          )
        : null;

    io?.observe(el);
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      io?.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return ref;
}

/** Page-wide scroll progress, 0..1 — drives the header rule. */
export function usePageProgress() {
  const [p, setP] = useState(0);

  useEffect(() => {
    let frame = 0;

    const measure = () => {
      frame = 0;
      const d = document.documentElement;
      const max = d.scrollHeight - d.clientHeight;
      setP(max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0);
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return p;
}

/** Counts from 0 to `to` once the node scrolls into view. */
export function useCountUp(to, { duration = 1400 } = {}) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (reduced() || !("IntersectionObserver" in window)) {
      setValue(to);
      return undefined;
    }

    let raf = 0;
    let start = 0;

    const step = (t) => {
      if (!start) start = t;
      const k = Math.min(1, (t - start) / duration);
      // ease-out cubic
      setValue(Math.round(to * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(step);
    };

    const io = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return;
        io.unobserve(el);
        raf = requestAnimationFrame(step);
      },
      { threshold: 0.4 }
    );

    io.observe(el);
    return () => {
      io.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [to, duration]);

  return [ref, value];
}
